// ** react imports
import React from 'react';

// ** router imports
import { Link, useNavigate } from 'react-router-dom';

// ** redux imports
import { useSelector } from 'react-redux';

// ** validation imports
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks
import { useResetPassword } from '../../../hooks/apis/useUsers';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import PasswordInput from '../../../components/common/Inputs/PasswordInput';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

// ** images imports
import Logo from '../../../images/Logo.png';

function ResetPasswordForm(props) {
  // props
  const {} = props;

  // hooks
  const navigate = useNavigate();
  const { forgotPasswordEmail } = useSelector(state => state.users);

  // custom hooks
  const { isResetPasswordLoading, resetPassword } = useResetPassword();

  // validation hooks
  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .required('Password is required')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/,
        'Password at least 8 characters, one special character, one uppercase, one lowercase, and one number'
      ),
    confirmPassword: yup
      .string()
      .required('Confirm Password is required')
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
  });

  // react hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  // handler
  const onSubmit = async data => {
    const response = await resetPassword({
      password: data.password,
      email: forgotPasswordEmail,
    });
    if (response.data.success) {
      navigate('/auth/signin');
    }
  };

  return (
    <div className="md:w-1/2">
      <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
        {/* Header */}
        <div className="flex-1">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <Link className="block" to="/">
              <div className="w-10 h-10">
                <img
                  src={Logo}
                  alt="Multi Tax"
                  className="h-full w-full object-contain"
                />
              </div>
            </Link>
          </div>
        </div>

        <div className="max-w-sm mx-auto w-full px-4 py-8">
          <h1 className="text-3xl text-gray-800 dark:text-gray-100 font-bold mb-6">
            Reset your Password
          </h1>
          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <PasswordInput
                  label="New password*"
                  name="password"
                  register={register}
                  errors={errors.password?.message}
                />
              </div>

              <div>
                <PasswordInput
                  label="Confirm password*"
                  name="confirmPassword"
                  register={register}
                  errors={errors.confirmPassword?.message}
                />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <LoadingButton
                type="submit"
                theme={BUTTON_THEMES.PRIMARY}
                isLoading={isResetPasswordLoading}
              >
                Reset Password
              </LoadingButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordForm;
